import { Link } from 'react-router-dom';
import { ArrowRight, Globe, Users, FileCheck, Handshake, Shield, BarChart3, Landmark, HeartPulse, ShoppingCart, Radio, Coins, Store, Code, Cloud, Settings, TestTube, Database, Layers, Building2, UsersRound, Puzzle, Wrench, MapPin, Clock, FileText, CheckCircle } from 'lucide-react';

const HERO_BG = 'https://images.unsplash.com/photo-1641760378661-6f290a50a62d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTV8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBjb3Jwb3JhdGUlMjBvZmZpY2UlMjBidWlsZGluZyUyMGdsYXNzfGVufDB8fHx8MTc3MjEwMjgzNnww&ixlib=rb-4.1.0&q=85';
const TEAM_IMG = 'https://images.unsplash.com/photo-1758873268631-fa944fc5cad2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjh8MHwxfHNlYXJjaHwxfHxjb3Jwb3JhdGUlMjBvZmZpY2UlMjB0ZWFtd29yayUyMGRpdmVyc2UlMjBwcm9mZXNzaW9uYWxzfGVufDB8fHx8MTc3MjQ0MDI1NXww&ixlib=rb-4.1.0&q=85';
const COLLAB_IMG = 'https://images.unsplash.com/photo-1758691736580-a41e0cfe9e9f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjh8MHwxfHNlYXJjaHwzfHxjb3Jwb3JhdGUlMjBvZmZpY2UlMjB0ZWFtd29yayUyMGRpdmVyc2UlMjBwcm9mZXNzaW9uYWxzfGVufDB8fHx8MTc3MjQ0MDI1NXww&ixlib=rb-4.1.0&q=85';

const services = [
  { icon: Globe, title: 'Global Staffing', desc: 'Access top talent across USA, Canada, and UK with our extensive global network.', path: '/global-staffing' },
  { icon: Users, title: 'IT Staffing', desc: 'Specialized IT professionals for contract, permanent, and project-based engagements.', path: '/it-staffing' },
  { icon: Handshake, title: 'Vendor Partnership', desc: 'Strategic partnerships with staffing firms for seamless talent delivery.', path: '/vendor-partnership' },
  { icon: FileCheck, title: 'Candidate Profile Marketing', desc: 'Professional profile presentation to maximize candidate placement success.', path: '/candidate-profile-marketing' },
];

const stats = [
  { number: '500+', label: 'Clients Served' },
  { number: '15K+', label: 'Placements Made' },
  { number: '3', label: 'Countries' },
  { number: '98%', label: 'Client Retention' },
];

const industries = [
  { icon: Landmark, title: 'Banking', desc: 'Core banking systems, regulatory compliance, risk management, and digital transformation talent.' },
  { icon: HeartPulse, title: 'Healthcare', desc: 'EHR specialists, health informatics, telemedicine platforms, and HIPAA-compliant solutions.' },
  { icon: ShoppingCart, title: 'Retail', desc: 'POS systems, supply chain technology, e-commerce platforms, and customer analytics.' },
  { icon: Radio, title: 'Telecom', desc: 'Network engineering, 5G implementation, billing systems, and infrastructure modernization.' },
  { icon: Coins, title: 'Fintech', desc: 'Payment processing, blockchain development, API integrations, and security engineering.' },
  { icon: Store, title: 'E-Commerce', desc: 'Platform development, marketplace architecture, performance optimization, and scalability.' },
];

const techExpertise = [
  { icon: Code, title: 'Java Developers', desc: 'Spring Boot, Microservices, J2EE, Hibernate, and enterprise Java ecosystems.' },
  { icon: Cloud, title: 'Cloud Engineers', desc: 'AWS, Azure, GCP architecture, migration, and cloud-native application development.' },
  { icon: Settings, title: 'DevOps Engineers', desc: 'CI/CD pipelines, Kubernetes, Docker, Terraform, and infrastructure automation.' },
  { icon: TestTube, title: 'QA Automation Engineers', desc: 'Selenium, Cypress, automated testing frameworks, performance and security testing.' },
  { icon: Database, title: 'Data Engineers', desc: 'ETL pipelines, data warehousing, Spark, Kafka, and real-time analytics platforms.' },
  { icon: Layers, title: 'Full Stack Developers', desc: 'React, Node.js, Python, .NET, and end-to-end application development.' },
];

const clientTypes = [
  { icon: Building2, title: 'Direct Clients', desc: 'Enterprise organizations and mid-market companies seeking IT talent for internal projects and digital transformation.' },
  { icon: UsersRound, title: 'Staffing Partners', desc: 'Staffing agencies and recruitment firms leveraging our talent network for their client requirements.' },
  { icon: Puzzle, title: 'System Integrators', desc: 'Global SI firms requiring specialized consultants for large-scale implementation and integration projects.' },
  { icon: Wrench, title: 'Implementation Firms', desc: 'Technology consulting firms needing skilled resources for ERP, CRM, and platform deployments.' },
];

const whyChoose = [
  { icon: MapPin, title: 'Canada-Based Delivery', desc: 'Headquartered in Scarborough, Ontario with local account management and North American compliance expertise.' },
  { icon: Shield, title: 'Pre-Screened Consultants', desc: 'Every candidate undergoes rigorous technical assessments, background verification, and reference checks.' },
  { icon: Clock, title: '24-48 Hour Submission', desc: 'Rapid candidate shortlisting and submission for urgent and standard staffing requirements.' },
  { icon: FileText, title: 'Vendor-Ready Documentation', desc: 'Professionally formatted resumes, skill matrices, and compliance documents ready for client submission.' },
];

const Home = () => {
  return (
    <div data-testid="home-page">
      {/* Hero Section - Dark */}
      <section data-testid="hero-section" className="relative min-h-[90vh] flex items-center bg-[#0A0A0A] overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_BG} alt="" className="w-full h-full object-cover opacity-30" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0A] via-[#0A0A0A]/80 to-transparent" />
        </div>
        <div className="sol-container relative z-10 py-32">
          <div className="max-w-2xl">
            <p className="text-xs uppercase tracking-[0.2em] text-white/50 font-semibold mb-6 animate-fade-in" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Canada-Based IT Staffing &amp; Talent Solutions
            </p>
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold text-white leading-[1.1] tracking-tight mb-6 animate-fade-in-up" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Building Teams<br />That Build Futures
            </h1>
            <p className="text-lg text-white/70 leading-relaxed mb-10 max-w-lg animate-fade-in-up delay-200" style={{ opacity: 0 }}>
              Canada-headquartered IT staffing partner supplying pre-screened IT professionals to clients across North America and UK.
            </p>
            <div className="flex flex-wrap gap-4 animate-fade-in-up delay-300" style={{ opacity: 0 }}>
              <Link to="/hire-talent" data-testid="hero-cta-hire" className="sol-btn-inverse">
                Hire Talent <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/join-talent-pool" data-testid="hero-cta-join" className="sol-btn-inverse">
                Join Talent Pool <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="bg-white border-b border-[#E5E5E5]">
        <div className="sol-container">
          <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-[#E5E5E5]">
            {stats.map((stat) => (
              <div key={stat.label} className="py-10 md:py-14 text-center">
                <div className="text-3xl md:text-4xl font-bold text-black mb-1" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  {stat.number}
                </div>
                <div className="text-sm text-[#525252]">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Overview */}
      <section data-testid="services-section" className="bg-white sol-section">
        <div className="sol-container">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-start">
            <div className="lg:col-span-4">
              <p className="sol-caption mb-4">What We Do</p>
              <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Enterprise Staffing Solutions
              </h2>
              <p className="text-[#525252] leading-relaxed mb-8">
                From contract staffing to permanent placements, we deliver tailored workforce solutions that align with your business objectives.
              </p>
              <Link to="/our-process" className="sol-btn-outline text-sm">
                Our Process <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="lg:col-span-8 grid grid-cols-1 md:grid-cols-2 gap-6">
              {services.map((svc) => (
                <Link
                  key={svc.path}
                  to={svc.path}
                  data-testid={`service-card-${svc.path.slice(1)}`}
                  className="sol-service-card group"
                >
                  <svc.icon className="w-6 h-6 mb-4 text-black" strokeWidth={1.5} />
                  <h3 className="text-lg font-semibold mb-2 group-hover:text-black" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    {svc.title}
                  </h3>
                  <p className="text-sm text-[#525252] leading-relaxed">{svc.desc}</p>
                  <ArrowRight className="w-4 h-4 mt-4 text-[#E5E5E5] group-hover:text-black transition-colors" />
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* About / Two Column */}
      <section className="bg-[#F5F5F5] sol-section">
        <div className="sol-container">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div className="sol-img-overlay aspect-[4/3]">
              <img src={TEAM_IMG} alt="Solnity team collaborating" className="w-full h-full object-cover" loading="lazy" />
            </div>
            <div>
              <p className="sol-caption mb-4">About Solnity</p>
              <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
                A Trusted Partner in<br />Global Talent Acquisition
              </h2>
              <p className="text-[#525252] leading-relaxed mb-6">
                Solnity is a Canada-based IT Staffing and Talent Solutions provider supporting clients across North America and the United Kingdom. We combine deep industry expertise with a rigorous vetting process to deliver exceptional talent.
              </p>
              <div className="grid grid-cols-2 gap-6 mb-8">
                <div className="flex items-start gap-3">
                  <Shield className="w-5 h-5 mt-0.5 text-black" strokeWidth={1.5} />
                  <div>
                    <h4 className="text-sm font-semibold mb-1" style={{ fontFamily: 'Manrope, sans-serif' }}>Pre-Vetted Talent</h4>
                    <p className="text-xs text-[#525252]">Rigorous screening and verification</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <BarChart3 className="w-5 h-5 mt-0.5 text-black" strokeWidth={1.5} />
                  <div>
                    <h4 className="text-sm font-semibold mb-1" style={{ fontFamily: 'Manrope, sans-serif' }}>Data-Driven</h4>
                    <p className="text-xs text-[#525252]">Analytics-backed hiring decisions</p>
                  </div>
                </div>
              </div>
              <Link to="/about" className="sol-btn-primary text-sm">
                Learn More <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Process Preview */}
      <section className="bg-white sol-section">
        <div className="sol-container">
          <div className="text-center mb-16">
            <p className="sol-caption mb-4">How We Work</p>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
              A Proven Process
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-6">
            {['Requirement Analysis', 'Candidate Screening', 'Resume Structuring', 'Client Submission', 'Interview Coordination', 'Deployment'].map((step, i) => (
              <div key={step} className="sol-step-card text-center">
                <div className="step-number mb-3">0{i + 1}</div>
                <h4 className="text-sm font-semibold" style={{ fontFamily: 'Manrope, sans-serif' }}>{step}</h4>
              </div>
            ))}
          </div>
          <div className="text-center mt-12">
            <Link to="/our-process" className="sol-btn-outline text-sm">
              View Full Process <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Why Choose Solnity */}
      <section data-testid="why-choose-section" className="bg-white sol-section">
        <div className="sol-container">
          <div className="text-center mb-16">
            <p className="sol-caption mb-4">Our Advantage</p>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Why Choose Solnity
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {whyChoose.map((item) => (
              <div key={item.title} data-testid={`why-choose-${item.title.toLowerCase().replace(/[\s–-]+/g, '-')}`} className="sol-service-card group text-center">
                <div className="w-14 h-14 flex items-center justify-center border border-[#E5E5E5] mx-auto mb-5 group-hover:border-black group-hover:bg-black transition-all duration-300">
                  <item.icon className="w-6 h-6 text-black group-hover:text-white transition-colors duration-300" strokeWidth={1.5} />
                </div>
                <h3 className="text-base font-semibold mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>{item.title}</h3>
                <p className="text-sm text-[#525252] leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trusted IT Talent Across Industries */}
      <section data-testid="industries-section" className="bg-[#F5F5F5] sol-section">
        <div className="sol-container">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-start">
            <div className="lg:col-span-4">
              <p className="sol-caption mb-4">Industries</p>
              <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Trusted IT Talent Across Industries
              </h2>
              <p className="text-[#525252] leading-relaxed">
                We deliver specialized IT professionals with deep domain knowledge across the industries that drive the modern economy.
              </p>
            </div>
            <div className="lg:col-span-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {industries.map((ind) => (
                <div key={ind.title} data-testid={`industry-card-${ind.title.toLowerCase()}`} className="sol-service-card bg-white group">
                  <div className="w-10 h-10 flex items-center justify-center border border-[#E5E5E5] mb-4 group-hover:border-black group-hover:bg-black transition-all duration-300">
                    <ind.icon className="w-5 h-5 text-black group-hover:text-white transition-colors duration-300" strokeWidth={1.5} />
                  </div>
                  <h3 className="text-sm font-semibold mb-1.5" style={{ fontFamily: 'Manrope, sans-serif' }}>{ind.title}</h3>
                  <p className="text-xs text-[#525252] leading-relaxed">{ind.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Technology Expertise */}
      <section data-testid="tech-expertise-section" className="bg-white sol-section">
        <div className="sol-container">
          <div className="text-center mb-16">
            <p className="sol-caption mb-4">Talent Specializations</p>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Technology Expertise
            </h2>
            <p className="text-[#525252] mt-4 max-w-2xl mx-auto">
              Access skilled professionals across the most in-demand technology roles and platforms.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {techExpertise.map((tech) => (
              <div key={tech.title} data-testid={`tech-card-${tech.title.toLowerCase().replace(/\s+/g, '-')}`} className="sol-service-card group">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 flex items-center justify-center border border-[#E5E5E5] group-hover:border-black group-hover:bg-black transition-all duration-300">
                    <tech.icon className="w-5 h-5 text-black group-hover:text-white transition-colors duration-300" strokeWidth={1.5} />
                  </div>
                  <h3 className="text-base font-semibold" style={{ fontFamily: 'Manrope, sans-serif' }}>{tech.title}</h3>
                </div>
                <p className="text-sm text-[#525252] leading-relaxed">{tech.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Who We Work With */}
      <section data-testid="who-we-work-with-section" className="bg-[#F5F5F5] sol-section">
        <div className="sol-container">
          <div className="text-center mb-16">
            <p className="sol-caption mb-4">Our Clients</p>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Who We Work With
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {clientTypes.map((ct) => (
              <div key={ct.title} data-testid={`client-type-${ct.title.toLowerCase().replace(/\s+/g, '-')}`} className="bg-white p-8 border border-[#E5E5E5] hover:shadow-xl hover:-translate-y-1 transition-all duration-300 text-center">
                <ct.icon className="w-7 h-7 mx-auto mb-5 text-black" strokeWidth={1.5} />
                <h3 className="text-base font-semibold mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>{ct.title}</h3>
                <p className="text-sm text-[#525252] leading-relaxed">{ct.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Strip - Dark */}
      <section data-testid="cta-strip" className="sol-cta-strip">
        <div className="sol-container">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-6">
            <div className="text-center md:text-left">
              <h3 className="text-xl font-semibold mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>Looking to Hire?</h3>
              <p className="text-sm text-white/60 mb-5">Find pre-vetted IT professionals for your team.</p>
              <Link to="/hire-talent" data-testid="cta-hire-talent" className="sol-btn-inverse text-sm">
                Hire Talent <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="text-center md:text-left md:border-x md:border-white/10 md:px-6">
              <h3 className="text-xl font-semibold mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>Partner With Us</h3>
              <p className="text-sm text-white/60 mb-5">Join our vendor network for staffing success.</p>
              <Link to="/become-vendor-partner" data-testid="cta-vendor-partner" className="sol-btn-inverse text-sm">
                Become a Vendor Partner <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="text-center md:text-left">
              <h3 className="text-xl font-semibold mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>Are You a Candidate?</h3>
              <p className="text-sm text-white/60 mb-5">Submit your profile and get discovered by top firms.</p>
              <Link to="/join-talent-pool" data-testid="cta-join-pool" className="sol-btn-inverse text-sm">
                Join Talent Pool <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Clients / Trust */}
      <section className="bg-[#F5F5F5] sol-section">
        <div className="sol-container">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div>
              <p className="sol-caption mb-4">Why Solnity</p>
              <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Trusted by Enterprises Worldwide
              </h2>
              <p className="text-[#525252] leading-relaxed mb-8">
                Our commitment to quality, speed, and transparency has earned us the trust of Fortune 500 companies and growing enterprises across Canada, the United States, and the United Kingdom.
              </p>
              <ul className="space-y-4">
                {[
                  'Dedicated account managers for every client',
                  'Average 72-hour time-to-submit for qualified candidates',
                  'Comprehensive compliance and background verification',
                  'Flexible engagement models tailored to your needs',
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3 text-sm text-[#333333]">
                    <div className="w-1.5 h-1.5 bg-black rounded-full mt-2 shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
            <div className="sol-img-overlay aspect-[4/3]">
              <img src={COLLAB_IMG} alt="Enterprise collaboration" className="w-full h-full object-cover" loading="lazy" />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
